

import React, { useContext, useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { PageTitle } from "../../PageTitle/PageTitle";
import { I18nReactContext } from "../../../contexts/I18nContext";
import './AdminCreateLearningPathPage.scss';
import saveIcon from '../../../assets/images/save-full-icon.png'
import {
    getAllAdminSignDocsAPI,
    getLearningPathBySignDocIdAPI,
    getSignDocApi,
    updateLearningPathAPI,
    saveLearningPathAPI
} from "../../../Services/api";
import { isLearningPathUnordered } from "../../../Utils/SignDocs/SignDocs";
import {useNavigateAfterSignDocSave} from "../../../Hooks/useNavigateAfterSignDocSave";
import {
    DndContext,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    closestCenter,
    pointerWithin
} from '@dnd-kit/core';
import { MgmntSignDocCard } from "../../SignDoc/MgmntSignDocCard/MgmntSignDocCard";
import { LearningPathSequence } from "../LearningPathSequence/LearningPathSequence";
import { getAllSignDocTypes, SignDocTypes, SIGNDOC_GROUP_TYPES} from "../../SignDoc/SignDocLib";
import { getAllSignDocCategories, SignDocCategories } from "../../../Utils/Constants";
import { SignDocsListWithFilters } from "../../LearningManagement/SignDocsListWithFilters/SignDocsListWithFilters";
import LoadingSpinner from "../../LoadingSpinner/LoadingSpinner";
import {find, get, values, isNull, forEach, includes, filter } from "lodash";
import {MainActionButton} from "../../Form/DominantButton/MainActionButton";
import { getAdminCreatedTypes } from "../../../Utils/TabFiltering/TabFilteringUtils";
import {
    calculateNewParent, findParentInGraph, useGraphState,
} from "../../../Utils/LearningPath/LearningPathGraph";
import {updateLearningPathOperation} from "../LearningPathLib";
import { DocumentRemovalAnimation } from '../RemovalAnimation/DocumentRemovalAnimation';
import { AlertDialog, AlertTypesEnum } from "../../Smart/AlertDialog/AlertDialog";

/**
 * AdminCreateLearningPathPage Component
 * 
 * This component provides an interface for administrators to create and edit learning paths.
 * It allows drag-and-drop functionality for organizing documents in a sequence and managing
 * the learning path structure.
 * 
 * Features:
 * - Drag and drop document organization
 * - Document removal with animation
 * - Tab-based document categorization
 * - Learning path sequence visualization
 * - Graph-based document relationships
 * 
 */

export const AdminCreateLearningPathPage = () => {
    const { signDocId } = useParams();
    const { i18n } = useContext(I18nReactContext);
    const navigateAfterSignDocSave = useNavigateAfterSignDocSave();

    // State management
    const [learningPathSignDoc, setLearningPathSignDoc] = useState();
    const [availableSignDocs, setAvailableSignDocs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [removalDocInfo, setRemovalDocInfo] = useState({
        documentId: null,
        signDoc: null
    });
    const [activeId, setActiveId] = useState(null);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [adminCreatedTypes, setAdminCreatedTypes] = useState([]);
    const { graph, updateGraphState, setGraph } = useGraphState(signDocId);

    // Free-path mode is read-only here; it's configured from CreateEditNewSignDocModal
    // (which sends the full SignDoc payload). This page just reflects the saved value
    // by showing the disclaimer banner + dimming the graph arrows.
    const isUnordered = isLearningPathUnordered(learningPathSignDoc);
    
    // Computed values
    const isEditMode = Boolean(signDocId);

    // Calculate SignDoc IDs to exclude (docs already in learning path + incompatible groupType)
    const excludedSignDocIds = useMemo(() => {
        const graphChildValues = values(Object.fromEntries(graph.edges));
        const allIds = [...Array.from(graph.edges.keys()), ...graphChildValues];
        // Also exclude the root learning path itself
        const existingExclusions = [...allIds, graph.rootId].filter(Boolean);

        // Exclude SignDocs with incompatible groupType
        // SEQUENTIAL LP → only SEQUENTIAL children allowed (exclude PARALLEL)
        // PARALLEL LP → all children allowed (no additional exclusions)
        const rootGroupType = get(learningPathSignDoc, 'groupType');
        if (rootGroupType === SIGNDOC_GROUP_TYPES.SEQUENTIAL) {
            const parallelIds = availableSignDocs
                .filter(doc => doc.groupType === SIGNDOC_GROUP_TYPES.PARALLEL)
                .map(doc => doc.id);
            return [...existingExclusions, ...parallelIds];
        }

        return existingExclusions;
    }, [graph, learningPathSignDoc, availableSignDocs]);

    // Drag and drop sensors configuration
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 8 }
        })
    );

    /**
     * Initializes the component by loading necessary data
     * - Fetches learning path data if in edit mode
     * - Loads available sign documents (for drag overlay and removal)
     * - Sets up initial graph structure
     */
    const init = async () => {
        setIsLoading(true);
        try {
            let learningPathSignDocData = await getSignDocApi({signDocId});
            setLearningPathSignDoc(learningPathSignDocData);

            // Load admin created types for filtering tabs
            const createdTypes = await getAdminCreatedTypes({
                categories: [SignDocCategories.FORCE_COMPLETION, SignDocCategories.KNOWLEDGE_CENTER],
                isForReportsComponent: false
            });
            setAdminCreatedTypes(createdTypes);

            // Load all available sign docs (needed for drag overlay and finding docs by ID)
            const allAvailableSignDocs = await getAllAdminSignDocsAPI({
                types: getAllSignDocTypes(),
                categories: [SignDocCategories.FORCE_COMPLETION, SignDocCategories.KNOWLEDGE_CENTER]
            });

            if (isEditMode) {
                // Get learning path data including nodes and edges
                const response = await getLearningPathBySignDocIdAPI({ signDocId });

                // Merge LP node data into available SignDocs so archived edge targets can be resolved
                // response.nodes contains SignDoc data for all edges regardless of status
                const availableIds = new Set((allAvailableSignDocs || []).map(doc => doc.id));
                const lpNodes = (response.nodes || [])
                    .filter(node => !availableIds.has(node.id))
                    .map(node => ({ id: node.id, type: node.type, ...node.data }));
                setAvailableSignDocs([...(allAvailableSignDocs || []), ...lpNodes]);

                // Build graph structure directly
                const newGraph = {
                    rootId: response.rootSignDocId || signDocId,
                    edges: new Map(),
                };

                // Process edges to build the graph
                if (response.edges && response.edges.length > 0) {
                    forEach(response.edges, ({ source, target }) => {
                        newGraph.edges.set(source, target);
                    });
                }

                setGraph(newGraph);
            } else {
                setAvailableSignDocs(allAvailableSignDocs || []);
                const newGraph = {
                    rootId: signDocId,
                    edges: new Map(),
                };
                setGraph(newGraph);
            }
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        init();
    }, [signDocId]);


    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    /**
     * Handles the end of a drag operation in the learning path editor
     * 
     * This function manages three types of operations:
     * 1. Reordering: Moving an existing document within the learning path
     * 2. Adding: Adding a new document to the learning path 
     * 
     * The function follows these steps:
     * 1. Validates the drag operation
     * 2. Determines if the operation is a reorder or add/remove
     * 3. Calculates the target parent for the document
     * 4. Makes the appropriate API call
     * 5. Updates the local graph state on success
     * 
     * @param {Object} event - The drag end event object
     * @param {Object} event.active - The document being dragged
     * @param {Object} event.over - The document being dropped onto
     * @param {Array<Object>} event.collisions - Array of documents that were collided with during the drag operation
     */
    const handleDragEnd = async (event) => {
        const { active, over, collisions } = event;
        setActiveId(null);
        if (!over || !active || active.id === over.id) return;

        const graphEdges = graph.edges;
        const graphChildValues = values(Object.fromEntries(graphEdges));
        const isReordering = graphEdges.has(active.id) || includes(graphChildValues, active.id);

        // For reordering, determine the landing position if there are multiple collisions
        let landOnDocId = null;
        if (isReordering && collisions.length > 1) {
            landOnDocId = collisions[1].id;
        }

        // Calculate the new parent document for the dragged item
        let targetParentId = calculateNewParent({
            overId: over.id,
            graph,
            allDocuments: availableSignDocs,
            isReordering,
            landOnDocId,
            actingId: active.id
        });

        if (isNull(targetParentId)) return;

        // For reordering, check if the target parent is different from current parent
        if (isReordering) {
            const sourceParentId = findParentInGraph({documentId: active.id, graph});
            
            // If source and target are the same, no operation needed
            if (sourceParentId === targetParentId) {
                return;
            }

            try {
                let ans = await updateLearningPathAPI({
                    rootSignDocId: graph.rootId,
                    operation: updateLearningPathOperation.REORDER,
                    parentSignDocId: sourceParentId,
                    newParentSignDocId: targetParentId,
                    documentId: active.id
                });
                if (!ans.errorMessage && ans.success) {
                    updateGraphState({
                        sourceParentId, 
                        parentId: targetParentId, 
                        documentId: active.id, 
                        operation: updateLearningPathOperation.REORDER
                    });
                } else {
                    throw new Error(ans.errorMessage || 'API call failed');
                }
            } catch (error) {
            }
        } else {
            // Handle adding new document
            try {
                let ans = await updateLearningPathAPI({
                    rootSignDocId: graph.rootId,
                    operation: updateLearningPathOperation.ADD,
                    parentSignDocId: targetParentId,
                    documentId: active.id
                });

                // Update local state on success
                if (!ans.errorMessage && ans.success) {
                    updateGraphState({
                        parentId: targetParentId,
                        documentId: active.id,
                        operation: updateLearningPathOperation.ADD
                    });
                } else {
                    throw new Error(ans.errorMessage || 'API call failed');
                }
            } catch (error) {
            }
        }
    };

    /**
     * Removes a document from the learning path
     * 
     * @param {Object} params - Parameters
     * @param {string} params.documentId - The ID of the document to remove
     * @returns {Promise<void>} A promise that resolves when the document is removed
     */
    const removeDocument = async ({documentId}) => {
        try {
            const parentId = findParentInGraph({documentId, graph});
            const signDoc = find(availableSignDocs, { id: documentId });
            
            let ans = await updateLearningPathAPI({
                rootSignDocId: graph.rootId,
                operation: updateLearningPathOperation.REMOVE,
                parentSignDocId: parentId,
                documentId,
            });

            if (!ans.errorMessage && ans.success) {
                // Set animation info and wait for completion
                setRemovalDocInfo({ documentId, signDoc });
                
                return new Promise((resolve) => {
                    // Animation will complete and then we'll update the graph
                    setTimeout(() => {
                        updateGraphState({operation: updateLearningPathOperation.REMOVE, parentId, documentId});
                        setRemovalDocInfo({ documentId: null, signDoc: null });
                        resolve();
                    }, 350); // Slightly longer than animation duration to ensure completion
                });
            } else {
                throw new Error(ans.errorMessage || 'API call failed');
            }
        } catch (error) {
            setRemovalDocInfo({ documentId: null, signDoc: null });
        }
    };

    /**
     * Handles the saving of the learning path
     * 
     * @returns {Promise<void>} A promise that resolves when the learning path is saved
     */
    const handleSave = async () => {
        try {
            if (graph.edges.size < 2) {
                setShowErrorDialog(true);
                return;
            }
            saveLearningPathAPI({ rootSignDocId: graph.rootId });
            setTimeout(() => {
                // Navigate to category tab after saving
                navigateAfterSignDocSave({
                    signDocType: get(learningPathSignDoc, 'type'),
                    signDocCategory: get(learningPathSignDoc, 'category'),
                    signDocId: graph.rootId,
                    navigateToTab: true
                });
            }, 500);
        } catch (error) {
        }
    }

    /**
     * Custom collision detection for drag and drop operations
     * @param {Object} args - Collision detection arguments containing information about the dragged item and potential drop targets
     * @returns {Array} Array of collision results, prioritized by drop zones and pointer position
     * 
     * This function implements a two-stage collision detection strategy:
     * 1. First checks for pointer-based collisions using pointerWithin()
     * 2. If pointer collisions exist, then checks for center-based collisions using closestCenter()
     * 
     * The collision detection prioritizes drop zones (elements with IDs starting with 'drop-zone')
     * over other elements. This ensures that when dragging items, they will preferentially
     * snap to designated drop zones rather than other elements in the interface.
     */
    const customCollisionDetection = (args) => {
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) {
            const centerCollisions = closestCenter(args);

            if (centerCollisions.length > 0) {
                // Find the closest drop zone collision
                const dropZoneCollision = centerCollisions.find(collision =>
                    collision.id && collision.id.toString().startsWith('drop-zone')
                );
                return dropZoneCollision ? [dropZoneCollision, pointerCollisions[0]] : [pointerCollisions[0]];
            }
            return [pointerCollisions[0]];
        }
        return [];
    }
    
    if (isLoading) return <LoadingSpinner />;
    
    return (
        <div className="AdminCreateLearningPathPage">
            <PageTitle
                title={`${i18n.translate('learningPath.editTitle')}: ${get(learningPathSignDoc, ['docName'], '')}`}
                customComponent={
                    <MainActionButton
                        title={i18n.translate('learningPath.buttons.saveLearningPath')}
                        subTitle={`${i18n.translate('learningPath.buttons.saveLearningPathSubTitle')}`}
                        image={saveIcon}
                        onClick={handleSave}
                        />}
                size={'small'}
                noLogo
            />
            {showErrorDialog && (
                <AlertDialog
                    title={i18n.translate('learningPath.errors.title')}
                    alertMessage={i18n.translate('learningPath.errors.noDocuments')}
                    type={AlertTypesEnum.ERROR}
                    onOk={() => setShowErrorDialog(false)}
                    okText={i18n.translate('learningPath.errors.okText')}
                />
            )}
            <div className="learningPathForm">
                <DndContext
                    sensors={sensors}
                    collisionDetection={customCollisionDetection}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <div className="learningPathLayout">
                        <div className="availableDocumentsContainer">
                            {/* Display-only banner — sits in the same column as the filters/search
                                so the left edge naturally aligns with the search input.
                                Free-path mode is configured in CreateEditNewSignDocModal;
                                this page just reflects the saved value. */}
                            {isUnordered && (
                                <div className="AdminCreateLearningPathPage__freePathBanner">
                                    {i18n.translate('learningPath.freePath.adminBanner')}
                                </div>
                            )}
                            <SignDocsListWithFilters
                                types={filter(getAllSignDocTypes(), type => type !== SignDocTypes.LEARNING_PATH)}
                                categories={[SignDocCategories.FORCE_COMPLETION, SignDocCategories.KNOWLEDGE_CENTER]}
                                filterableTypes={filter(adminCreatedTypes, type => type !== SignDocTypes.LEARNING_PATH)}
                                showFilters={{search: true, type: true}}
                                draggable={true}
                                noButton={true}
                                excludeSignDocIds={excludedSignDocIds}
                                disclaimerText={get(learningPathSignDoc, 'groupType') === SIGNDOC_GROUP_TYPES.SEQUENTIAL
                                    ? i18n.translate('learningPath.sequentialOnlyDisclaimer')
                                    : null}
                            />
                        </div>

                        <div className={`learningPathSequenceContainer ${isUnordered ? 'learningPathSequenceContainer--unordered' : ''}`}>
                            <LearningPathSequence
                                graph={graph}
                                onRemoveDocument={removeDocument}
                                allDocuments={availableSignDocs}
                            />
                        </div>
                    </div>

                    <DragOverlay>
                        {activeId ? (
                            <div className="dragOverlay">
                                <MgmntSignDocCard
                                    signDoc={find(availableSignDocs, { id: activeId })}
                                    noButton={true}
                                />
                            </div>
                        ) : null}
                    </DragOverlay>

                    <DocumentRemovalAnimation
                        documentId={removalDocInfo.documentId}
                        signDoc={removalDocInfo.signDoc}
                        tabContainerSelector=".availableDocumentsContainer"
                        documentIdPrefix="path-document-"
                    />
                </DndContext>
            </div>
        </div>
    );
};